/* Huedata Academy course runtime.
 *
 * The one-page courses (Owning a Color, Color, Light and Health in Built
 * Space, Accessible Color) were built against a small page API: window.claude
 * .use("db" | "user" | "sample" | "downloads"). This file provides that API
 * on huedata.ai, backed by the Huedata server:
 *
 *   db         the learner's course document, the cohort's documents and the
 *              live-class session  -> /api/learn/courses/:course/...
 *   user       the learner's opaque id, short name and teacher flag
 *   sample     "Ask HueAI": one chat turn per call to /api/learn/ask; the page
 *              runs its own tools on its own data
 *   downloads  saves a file in the browser
 *
 * It also reports each unit's steps, Check score and completion to
 * /api/learn/progress (HD_UNIT), which is what My learning and the course
 * certificate read, and shows the unlock panel for HSW units the learner has
 * not unlocked (HD_LOCKED_GO).
 */
(function () {
  "use strict";
  var API = window.HD_API != null ? window.HD_API : "https://api.huedata.ai";
  var COURSE = window.HD_SLUG;
  var BASE = "/api/learn/courses/" + COURSE;

  function req(method, path, body) {
    return fetch(API + path, {
      method: method, credentials: "include", keepalive: method !== "GET" && !!body && JSON.stringify(body).length < 60000,
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      body: body ? JSON.stringify(body) : undefined
    }).then(function (r) {
      if (!r.ok) {
        var e = new Error("HTTP " + r.status); e.status = r.status;
        e.code = r.status === 413 ? "quota_exceeded" : r.status === 429 ? "rate_limited" : r.status >= 500 ? "unavailable" : "error";
        throw e;
      }
      return r.json();
    });
  }
  window.HD_REQ = req;
  window.HD_FILE = function (name) { return API + "/api/learn/files/" + COURSE + "/" + name; };

  function snap(id, data) { return { id: id, exists: data != null, data: function () { return data; } }; }
  function poll(fetcher, ms) {
    var stopped = false;
    (function tick() {
      if (stopped) return;
      fetcher().catch(function () {}).then(function () { if (!stopped) setTimeout(tick, document.hidden ? ms * 4 : ms); });
    })();
    return function () { stopped = true; };
  }

  function sessionRef() {
    function get() { return req("GET", BASE + "/session").then(function (r) { return snap("live", r.data); }); }
    return {
      get: get,
      set: function (d) { return req("PUT", BASE + "/session", { data: d }); },
      onSnapshot: function (cb, err) { return poll(function () { return get().then(cb, err); }, 10000); }
    };
  }
  function docRef(path) {
    var parts = String(path).split("/"), coll = parts[0], id = parts[1];
    if (coll === "session") return sessionRef();
    function get() { return req("GET", BASE + "/docs/" + coll + "/mine").then(function (r) { return snap(id, r.data); }); }
    return {
      get: get,
      set: function (d) { return req("PUT", BASE + "/docs/" + coll + "/mine", { data: d }); },
      onSnapshot: function (cb, err) { return poll(function () { return get().then(cb, err); }, 15000); }
    };
  }
  function collectionRef(coll) {
    return {
      onSnapshot: function (cb, err) {
        var cache = {}, asOf = null;
        return poll(function () {
          return req("GET", BASE + "/docs/" + coll + (asOf ? "?since=" + encodeURIComponent(asOf) : "")).then(function (r) {
            var docs = r.docs || {}, changed = Object.keys(docs);
            changed.forEach(function (k) { cache[k] = docs[k]; });
            var first = asOf == null; asOf = r.as_of || asOf;
            if (first || changed.length) cb({ docs: Object.keys(cache).map(function (k) { return snap(k, cache[k]); }) });
          }, err);
        }, 20000);
      }
    };
  }

  /* A small initials avatar, so no photo or email ever leaves the server. */
  var HUES = ["#2B7BFF", "#7B2FF7", "#E8177E", "#FF8A1E", "#00B37A", "#00A3C4"];
  function avatar(name, id) {
    var s = String(name || "?").trim(), parts = s.split(/\s+/), ini = ((parts[0] || "?")[0] + ((parts[1] || "")[0] || "")).toUpperCase();
    var h = 0; String(id || s).split("").forEach(function (c) { h = (h * 31 + c.charCodeAt(0)) >>> 0; });
    var svg = '<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64"><rect width="64" height="64" rx="32" fill="' + HUES[h % HUES.length] +
      '"/><text x="32" y="41" font-family="Helvetica,Arial,sans-serif" font-size="26" font-weight="700" fill="#fff" text-anchor="middle">' +
      ini.replace(/[<&>"]/g, "") + "</text></svg>";
    return "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg);
  }
  var meP = null;
  function me() { return meP || (meP = req("GET", BASE + "/me")); }
  var userApi = {
    me: function () { return me().then(function (r) { return { id: r.id, name: r.name, avatarUrl: avatar(r.name, r.id) }; }); },
    canEdit: function () { return me().then(function (r) { return !!r.teacher; }); },
    profiles: function (ids) {
      return req("POST", BASE + "/profiles", { ids: ids }).then(function (r) {
        var out = {}, p = r.profiles || {};
        Object.keys(p).forEach(function (k) { out[k] = { name: p[k].name, avatarUrl: avatar(p[k].name, k) }; });
        return out;
      });
    }
  };

  /* Ask HueAI: the page passes its own tools; each round trip relays one chat
     turn through the server, then runs any tool calls here, on the page's data. */
  function sampler(prompt, opts) {
    opts = opts || {};
    var tools = (opts.tools || []).map(function (t) {
      return { type: "function", "function": { name: t.name, description: t.description, parameters: t.inputSchema || { type: "object", properties: {} } } };
    });
    var byName = {}; (opts.tools || []).forEach(function (t) { byName[t.name] = t; });
    var messages = [{ role: "user", content: prompt }];
    function fail(code) { var e = new Error(code); e.code = code; return e; }
    function round(n) {
      if (opts.signal && opts.signal.aborted) return Promise.reject(fail("cancelled"));
      if (n > 6) return Promise.reject(fail("error"));
      return req("POST", "/api/learn/ask", { course: COURSE, messages: messages, tools: tools }).then(function (r) {
        var m = r.message || {};
        if (opts.signal && opts.signal.aborted) throw fail("cancelled");
        if (m.tool_calls && m.tool_calls.length) {
          messages.push({ role: "assistant", content: m.content || null, tool_calls: m.tool_calls });
          return Promise.all(m.tool_calls.map(function (c) {
            var t = byName[c["function"] && c["function"].name], out;
            try { out = t ? t.execute(JSON.parse(c["function"].arguments || "{}")) : { error: "unknown tool" }; } catch (e) { out = { error: "tool failed" }; }
            return Promise.resolve(out).then(function (v) { messages.push({ role: "tool", tool_call_id: c.id, content: JSON.stringify(v).slice(0, 12000) }); });
          })).then(function () { return round(n + 1); });
        }
        var text = String(m.content || "");
        if (opts.onText) opts.onText({ text: text });
        return { text: text };
      }, function (e) {
        throw fail(e.status === 503 ? "sampling_disabled" : e.status === 429 ? "rate_limited" : e.status === 403 ? "not_granted" : "error");
      });
    }
    return round(1);
  }
  var sampleP = null;
  function sample() {
    return sampleP || (sampleP = req("GET", "/api/learn/ask/status").then(function (r) { return r.available ? sampler : null; }, function () { return null; }));
  }

  var downloads = {
    save: function (o) {
      var blob = o.data instanceof Blob ? o.data : new Blob([o.data], { type: o.mimeType || o.type || (/\.csv$/i.test(o.filename) ? "text/csv" : "application/octet-stream") });
      var a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = o.filename || "download";
      document.body.appendChild(a); a.click();
      setTimeout(function () { URL.revokeObjectURL(a.href); a.remove(); }, 1000);
      return Promise.resolve(true);
    }
  };

  window.claude = {
    use: function (name) {
      if (name === "db") return Promise.resolve({ doc: docRef, collection: collectionRef });
      if (name === "user") return Promise.resolve(userApi);
      if (name === "sample") return sample();
      if (name === "downloads") return Promise.resolve(downloads);
      return Promise.resolve(null);
    }
  };

  /* Unit progress for My learning and the certificate. Called by the page on
     every save with the current unit's state, quiz and steps. */
  var STEP_NAMES = ["watch", "try", "read", "decide", "practice", "check", "lab", "wrap"];
  var sent = {}, timers = {};
  window.HD_UNIT = function (unit, s, quiz, steps) {
    if (!unit || !s) return;
    var n = (steps || []).length || 8, done = [];
    for (var i = 0; i < n && i < STEP_NAMES.length; i++) if (s.visited && s.visited[i]) done.push(STEP_NAMES[i]);
    if (s.commit != null) done.push("decide");
    if (s.specIn) done.push("practice");
    var q = quiz || [], answered = q.length && q.every(function (x, k) { return s.quiz && s.quiz[k] && s.quiz[k].first != null; });
    var right = null, total = null;
    if (answered) { done.push("check"); total = q.length; right = q.filter(function (x, k) { return s.quiz[k].first === x.a; }).length; }
    if (s.done) done.push("wrap");
    var body = { course: COURSE, unit: unit, steps: done.filter(function (v, i, a) { return a.indexOf(v) === i; }), check_right: right, check_total: total, finished: !!s.done };
    var key = JSON.stringify(body);
    if (sent[unit] === key) return;
    clearTimeout(timers[unit]);
    timers[unit] = setTimeout(function () {
      req("POST", "/api/learn/progress", body).then(function () { sent[unit] = key; }, function () {});
    }, s.done ? 0 : 1500);
  };

  /* HSW courses sold by the hour: the panel for a unit not yet unlocked. */
  window.HD_LOCKED_GO = function (unit, meta) {
    var boot = window.HD_COURSE || {}, credits = boot.credits || 0, old = document.getElementById("hdLock");
    if (old) old.remove();
    var el = document.createElement("div");
    el.id = "hdLock";
    el.setAttribute("role", "dialog"); el.setAttribute("aria-modal", "true"); el.setAttribute("aria-labelledby", "hdLockH");
    el.style.cssText = "position:fixed;inset:0;z-index:90;background:rgba(11,10,12,.55);display:flex;align-items:center;justify-content:center;padding:16px";
    var t = (meta && meta.title) ? String(meta.title).replace(/[<&>]/g, "") : "This unit";
    el.innerHTML = '<div style="background:var(--paper,#fff);color:var(--ink,#0B0A0C);max-width:460px;width:100%;border-radius:18px;padding:26px;display:flex;flex-direction:column;gap:12px;font-family:inherit">' +
      '<p style="margin:0;font-size:12.5px;letter-spacing:.14em;text-transform:uppercase;opacity:.65">Unit ' + ((meta && meta.n) || "") + ' &middot; 1 HSW hour</p>' +
      '<h2 id="hdLockH" style="margin:0;font-size:26px;line-height:1.15;font-weight:600">' + t + "</h2>" +
      '<p style="margin:0;font-size:16px;line-height:1.5;opacity:.8">' + (credits > 0
        ? "You have " + credits + " hour credit" + (credits > 1 ? "s" : "") + ". Use one to open this unit; it stays yours."
        : "Each unit is its own one-hour course. Buy an hour, the 12-hour pack or the all-access pass to open it.") + "</p>" +
      '<div style="display:flex;gap:10px;flex-wrap:wrap;margin-top:6px">' +
      (credits > 0 ? '<button type="button" id="hdUse" style="min-height:44px;padding:0 20px;border-radius:999px;border:0;background:#0B0A0C;color:#fff;font:inherit;font-weight:600;cursor:pointer">Use 1 credit</button>'
        : '<a href="/hueex/#courses" style="display:inline-flex;align-items:center;min-height:44px;padding:0 20px;border-radius:999px;background:#0B0A0C;color:#fff;font-weight:600;text-decoration:none">See prices</a>') +
      '<button type="button" id="hdClose" style="min-height:44px;padding:0 20px;border-radius:999px;border:1px solid currentColor;background:none;color:inherit;font:inherit;cursor:pointer">Not now</button></div>' +
      '<p id="hdMsg" role="status" style="margin:0;font-size:14px;opacity:.75"></p></div>';
    document.body.appendChild(el);
    function close() { el.remove(); }
    el.addEventListener("click", function (e) { if (e.target === el) close(); });
    document.getElementById("hdClose").addEventListener("click", close);
    var use = document.getElementById("hdUse");
    if (use) use.addEventListener("click", function () {
      use.disabled = true; document.getElementById("hdMsg").textContent = "Opening the unit…";
      req("POST", "/api/learn/redeem", { course: COURSE, unit: unit }).then(function () {
        location.hash = unit; location.reload();
      }, function () { use.disabled = false; document.getElementById("hdMsg").textContent = "That did not work. Please try again."; });
    });
    (document.getElementById("hdUse") || document.getElementById("hdClose")).focus();
  };
})();
